import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { productsAPI, recipeIngredientsAPI, fdsAPI, aiAPI, productInfoAPI } from '@/utils/api';
import { Beaker, Trash2, ChevronRight, Factory, ExternalLink, Upload, Download, FileText, CheckCircle2, AlertCircle, X, Sparkles, Lock, Unlock, Image, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const Products = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [linkedProducts, setLinkedProducts] = useState({});
  const [dipStatuses, setDipStatuses] = useState({});
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [ingredientsDialog, setIngredientsDialog] = useState({ open: false, productId: null, productName: '' });
  const [infoDialog, setInfoDialog] = useState({ open: false, product: null });
  const [ingredients, setIngredients] = useState([]);
  const [loadingIngredients, setLoadingIngredients] = useState(false);
  const [uploadingFds, setUploadingFds] = useState(null);
  const [translatingInci, setTranslatingInci] = useState(false);
  const [uploadingLabel, setUploadingLabel] = useState(false);
  const [labelDialog, setLabelDialog] = useState({ open: false, product: null });

  // Info form
  const [infoForm, setInfoForm] = useState({ efficacy: '', functions: '', body_zone: '', usage_instructions: '', inci_list: '' });

  useEffect(() => { loadProducts(); }, []);

  const loadProducts = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await productsAPI.getAll(user.id || 'demo-user');
      setProducts(res.data);
      const links = {};
      const statuses = {};
      for (const p of res.data) {
        try {
          const linkRes = await fetch(`${API_BASE}/api/products/${p.id}/linked-finished-product`);
          const linkData = await linkRes.json();
          if (linkData.found) links[p.id] = linkData.finished_product_id;
        } catch (e) {}
        try {
          const stRes = await fetch(`${API_BASE}/api/dip/status/${p.id}`);
          if (stRes.ok) statuses[p.id] = await stRes.json();
        } catch (e) {}
      }
      setLinkedProducts(links);
      setDipStatuses(statuses);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally { setLoading(false); }
  };

  const handleDownloadDIP = async (product) => {
    try {
      const res = await fetch(`${API_BASE}/api/dip/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: product.id }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erreur lors de la génération');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `DIP_${product.name.replace(/ /g, '_')}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('DIP téléchargé');
    } catch (e) { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce produit DIP ?')) return;
    try { await productsAPI.delete(id); toast.success('Produit supprimé'); loadProducts(); } catch (e) { toast.error('Erreur'); }
  };

  const handleFormulaClick = (product) => {
    const fpId = linkedProducts[product.id];
    if (fpId) navigate(`/production/product/${fpId}/recipe`);
    else { toast.info("Créez d'abord la recette dans Production"); navigate('/production/products'); }
  };

  // === INGREDIENTS + FDS ===
  const openIngredientsDialog = async (product) => {
    setIngredientsDialog({ open: true, productId: product.id, productName: product.name });
    setLoadingIngredients(true);
    try {
      const res = await recipeIngredientsAPI.getByDipProduct(product.id);
      const ings = res.data;
      setIngredients(ings);
      // Auto-traduction INCI si des ingrédients n'ont pas encore de nom INCI
      if (ings.length > 0 && ings.some(i => !i.inci_name)) {
        autoTranslateInci(ings, product.id);
      }
    }
    catch (e) { setIngredients([]); }
    finally { setLoadingIngredients(false); }
  };

  const autoTranslateInci = async (ings, productId) => {
    setTranslatingInci(true);
    try {
      const res = await aiAPI.translateInci(ings.map(i => ({ name: i.material_name, quantity_gr: i.quantity_gr })));
      const inciList = res.data.inci_list || '';
      setInfoForm(prev => ({ ...prev, inci_list: inciList }));
      await productInfoAPI.update(productId, { inci_list: inciList });
      if (res.data.per_ingredient && res.data.per_ingredient.length > 0) {
        const updated = ings.map((ing, idx) => {
          const match = res.data.per_ingredient.find(p => p.name === ing.material_name) || res.data.per_ingredient[idx];
          const inciName = match ? match.inci : '';
          // Sauvegarder le nom INCI dans la composition
          if (inciName && ing.composition_id) {
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recipe-compositions/${ing.composition_id}/inci`, {
              method: 'PUT', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ inci_name: inciName })
            });
          }
          return { ...ing, inci_name: inciName };
        });
        setIngredients(updated);
      }
    } catch (e) { /* silencieux */ }
    finally { setTranslatingInci(false); }
  };

  const handleInciEdit = async (index, newInci) => {
    setIngredients(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], inci_name: newInci };
      return updated;
    });
  };

  const handleInciSave = async (index) => {
    const ing = ingredients[index];
    if (!ing.composition_id) return;
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recipe-compositions/${ing.composition_id}/inci`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ inci_name: ing.inci_name || '' })
      });
      // Mettre à jour la liste INCI globale
      const globalInci = ingredients.map(i => i.inci_name).filter(Boolean).join(', ');
      setInfoForm(prev => ({ ...prev, inci_list: globalInci }));
      await productInfoAPI.update(ingredientsDialog.productId, { inci_list: globalInci });
    } catch (e) { /* silencieux */ }
  };

  const handleFdsUpload = async (materialId, file) => {
    setUploadingFds(materialId);
    try { await fdsAPI.upload(materialId, file); toast.success('FDS uploadée'); const res = await recipeIngredientsAPI.getByDipProduct(ingredientsDialog.productId); setIngredients(res.data); }
    catch (e) { toast.error("Erreur upload"); }
    finally { setUploadingFds(null); }
  };
  const handleFdsDelete = async (fdsId) => {
    try { await fdsAPI.delete(fdsId); toast.success('FDS supprimée'); const res = await recipeIngredientsAPI.getByDipProduct(ingredientsDialog.productId); setIngredients(res.data); }
    catch (e) { toast.error('Erreur'); }
  };
  const handleFdsDownload = async (fdsId, filename) => {
    try { const res = await fdsAPI.download(fdsId); const url = window.URL.createObjectURL(new Blob([res.data])); const a = document.createElement('a'); a.href = url; a.download = filename; a.click(); window.URL.revokeObjectURL(url); }
    catch (e) { toast.error('Erreur téléchargement'); }
  };

  // === PRODUCT INFO ===
  const openInfoDialog = (product) => {
    setInfoDialog({ open: true, product });
    setInfoForm({
      efficacy: product.efficacy || '', functions: product.functions || '',
      body_zone: product.body_zone || '', usage_instructions: product.usage_instructions || '',
      inci_list: product.inci_list || ''
    });
  };

  const handleSaveInfo = async () => {
    try {
      await productInfoAPI.update(infoDialog.product.id, infoForm);
      toast.success('Informations sauvegardées');
      loadProducts();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleLabelUpload = async (productId, file) => {
    setUploadingLabel(true);
    try {
      await productInfoAPI.uploadLabel(productId, file);
      toast.success('Étiquette uploadée');
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await productsAPI.getAll(currentUser.id || 'demo-user');
      setProducts(res.data);
      const updatedProduct = res.data.find(p => p.id === productId);
      if (updatedProduct) setLabelDialog(prev => ({ ...prev, product: updatedProduct }));
    }
    catch (e) { toast.error('Erreur upload'); }
    finally { setUploadingLabel(false); }
  };
  const handleLabelDelete = async (productId, labelId) => {
    try {
      await fetch(`${API_BASE}/api/products/${productId}/label/${labelId}`, { method: 'DELETE' });
      toast.success('Étiquette supprimée');
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await productsAPI.getAll(currentUser.id || 'demo-user');
      setProducts(res.data);
      const updatedProduct = res.data.find(p => p.id === productId);
      if (updatedProduct) setLabelDialog(prev => ({ ...prev, product: updatedProduct }));
    } catch (e) { toast.error('Erreur'); }
  };
  const handleLabelDownload = async (productId, labelId, filename) => {
    try {
      const res = await fetch(`${API_BASE}/api/products/${productId}/label/${labelId}/download`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Erreur'); }
  };

  const handleLockRecipe = async (productId) => {
    try { await productInfoAPI.lockRecipe(productId); toast.success('Recette verrouillée'); loadProducts(); }
    catch (e) { toast.error('Erreur'); }
  };
  const handleUnlockRecipe = async (productId) => {
    if (!window.confirm('Déverrouiller la recette ? Cela sera signalé dans le DIP.')) return;
    try { await productInfoAPI.unlockRecipe(productId); toast.success('Recette déverrouillée — modification signalée'); loadProducts(); }
    catch (e) { toast.error('Erreur'); }
  };

  const handleAcknowledgeModification = async (productId) => {
    try { await productInfoAPI.acknowledgeModification(productId); toast.success('Modification validée'); loadProducts(); }
    catch (e) { toast.error('Erreur'); }
  };

  if (loading) return <div className="p-8"><div className="skeleton-shimmer h-64 rounded-lg"></div></div>;

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-semibold text-foreground mb-2 tracking-tight">Produits DIP</h1>
          <p className="text-base text-muted-foreground">Gérez vos Dossiers d'Information Produit</p>
        </div>
        <Button data-testid="btn-go-production" variant="outline" className="gap-2" onClick={() => navigate('/production/products')}>
          <Factory size={18} /> Créer un produit dans Production <ExternalLink size={14} />
        </Button>
      </div>

      {products.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <Factory className="mx-auto mb-4 text-muted-foreground" size={48} />
            <h3 className="text-xl font-medium mb-2">Aucun produit DIP</h3>
            <p className="text-muted-foreground mb-6">Créez d'abord un produit dans Production.</p>
            <Button onClick={() => navigate('/production/products')}><Factory size={18} className="mr-2" /> Aller dans Production</Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product, index) => (
            <Card key={product.id} className="hover:border-primary/20 hover:shadow-md transition-all duration-200 slide-up" style={{ animationDelay: `${index * 0.05}s` }}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Beaker className="text-primary" size={24} strokeWidth={1.5} />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{product.name}</CardTitle>
                      <CardDescription>{product.type || 'Produit cosmétique'}</CardDescription>
                    </div>
                  </div>
                  {product.recipe_locked ? (
                    <Button size="sm" variant="ghost" className="text-green-600" onClick={() => handleUnlockRecipe(product.id)} title="Recette verrouillée">
                      <Lock size={16} />
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => handleLockRecipe(product.id)} title="Verrouiller la recette">
                      <Unlock size={16} />
                    </Button>
                  )}
                </div>
                {product.recipe_modified_after_lock && (
                  <div className="mt-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700 font-medium flex items-center justify-between gap-2">
                    <span>Une modification de recette a été apportée</span>
                    <Button size="sm" variant="outline" className="h-6 text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
                      data-testid={`btn-acknowledge-${product.id}`}
                      onClick={(e) => { e.stopPropagation(); handleAcknowledgeModification(product.id); }}>
                      Modification validée
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                {(() => {
                  const st = dipStatuses[product.id];
                  const isComplete = st && st.has_formula && st.has_manufacturing && st.has_toxicology && st.has_labels;
                  if (isComplete) {
                    return (
                      <>
                        <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1.5">
                          <CheckCircle2 size={14} /> DIP complet — prêt à télécharger
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Button data-testid={`btn-download-dip-${product.id}`}
                            className="w-full gap-2 bg-[#205F79] hover:bg-[#184a5e]"
                            onClick={() => handleDownloadDIP(product)}>
                            <Download size={16} /> Télécharger
                          </Button>
                          <Button data-testid={`btn-edit-dip-${product.id}`}
                            variant="outline"
                            className="w-full gap-2"
                            onClick={() => navigate(`/product/${product.id}/formula`)}>
                            Modifier <ChevronRight size={14} />
                          </Button>
                        </div>
                      </>
                    );
                  }
                  return (
                    <Button data-testid={`btn-complete-dip-${product.id}`}
                      className="w-full gap-2 h-11 bg-[#205F79] hover:bg-[#184a5e]"
                      onClick={() => navigate(`/product/${product.id}/formula`)}>
                      Compléter le DIP <ChevronRight size={16} />
                    </Button>
                  );
                })()}
                <Button size="sm" variant="ghost" className="w-full gap-2 text-destructive hover:text-destructive" onClick={() => handleDelete(product.id)}>
                  <Trash2 size={14} /> Supprimer
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog Ingrédients + FDS + INCI */}
      <Dialog open={ingredientsDialog.open} onOpenChange={(open) => setIngredientsDialog({ ...ingredientsDialog, open })}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Beaker size={20} /> Ingrédients — {ingredientsDialog.productName}
            </DialogTitle>
          </DialogHeader>
          {loadingIngredients ? (
            <div className="py-8 text-center text-muted-foreground">Chargement...</div>
          ) : ingredients.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-muted-foreground mb-4">Aucun ingrédient dans la recette</p>
              <Button variant="outline" onClick={() => { setIngredientsDialog({ ...ingredientsDialog, open: false }); handleFormulaClick({ id: ingredientsDialog.productId }); }}>
                Aller dans la recette Production
              </Button>
            </div>
          ) : (
            <div className="space-y-3 mt-4">
              {ingredients.map((ing, i) => (
                <div key={i} className="p-4 rounded-lg border border-border hover:bg-muted/30 transition-colors space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 mr-3">
                      <p className="text-sm font-medium">{ing.material_name}</p>
                      {ing.supplier && <p className="text-xs text-muted-foreground">Fournisseur : {ing.supplier}</p>}
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="font-mono text-xs px-2 py-1 bg-muted rounded">{ing.quantity_gr} gr</span>
                      <span className="font-mono text-xs px-2 py-1 bg-primary/10 text-primary rounded">{ing.percentage}%</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground shrink-0 w-10">INCI:</span>
                    <Input
                      data-testid={`input-inci-${i}`}
                      className="h-7 text-xs font-mono flex-1"
                      placeholder={translatingInci ? 'Traduction en cours...' : 'Nom INCI'}
                      value={ing.inci_name || ''}
                      onChange={(e) => handleInciEdit(i, e.target.value)}
                      onBlur={() => handleInciSave(i)}
                    />
                  </div>
                  <div className="border-t border-border/50 pt-2">
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">FDS</p>
                      <label className="cursor-pointer">
                        <input type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.png" onChange={(e) => { if (e.target.files[0]) handleFdsUpload(ing.material_id, e.target.files[0]); e.target.value=''; }} />
                        <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" asChild disabled={uploadingFds === ing.material_id}>
                          <span><Upload size={12} /> {uploadingFds === ing.material_id ? '...' : 'Ajouter FDS'}</span>
                        </Button>
                      </label>
                    </div>
                    {ing.fds_count === 0 ? (
                      <div className="text-xs text-muted-foreground italic">FDS optionnelle (déjà incluse dans le rapport toxicologique)</div>
                    ) : (
                      <div className="space-y-1">
                        {ing.fds_files.map((fds, j) => (
                          <div key={j} className="flex items-center gap-2 text-xs bg-muted/50 rounded px-2 py-1.5">
                            <CheckCircle2 size={13} className="text-green-600 shrink-0" />
                            <span className="truncate flex-1">{fds.filename}</span>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => handleFdsDownload(fds.id, fds.filename)}><Download size={13} /></Button>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive" onClick={() => handleFdsDelete(fds.id)}><X size={13} /></Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {/* Liste INCI globale (auto-générée) */}
              <div className="border-t border-border pt-4 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Liste INCI complète</p>
                  {translatingInci && <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Loader2 size={12} className="animate-spin" /> Traduction auto...</span>}
                </div>
                <Textarea
                  className="text-sm min-h-[60px] font-mono"
                  placeholder="Les noms INCI sont générés automatiquement..."
                  value={infoForm.inci_list}
                  onChange={(e) => {
                    setInfoForm(prev => ({ ...prev, inci_list: e.target.value }));
                    productInfoAPI.update(ingredientsDialog.productId, { inci_list: e.target.value });
                  }}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Infos Produit */}
      <Dialog open={infoDialog.open} onOpenChange={(open) => setInfoDialog({ ...infoDialog, open })}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Informations Produit — {infoDialog.product?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Fonctions du produit</label>
              <Textarea className="text-sm" placeholder="Ex: Hydratant, nourrissant, protecteur..." value={infoForm.functions} onChange={(e) => setInfoForm({...infoForm, functions: e.target.value})} />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Efficacité</label>
              <Textarea className="text-sm" placeholder="Ex: Hydrate la peau pendant 24h..." value={infoForm.efficacy} onChange={(e) => setInfoForm({...infoForm, efficacy: e.target.value})} />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Zone du corps</label>
              <Input className="text-sm" placeholder="Ex: Visage, lèvres, corps..." value={infoForm.body_zone} onChange={(e) => setInfoForm({...infoForm, body_zone: e.target.value})} />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Consignes d'utilisation</label>
              <Textarea className="text-sm" placeholder="Ex: Appliquer matin et soir sur peau propre..." value={infoForm.usage_instructions} onChange={(e) => setInfoForm({...infoForm, usage_instructions: e.target.value})} />
            </div>
            <Button className="w-full" onClick={handleSaveInfo}>Sauvegarder</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Étiquettes multiples */}
      <Dialog open={labelDialog.open} onOpenChange={(v) => setLabelDialog({ ...labelDialog, open: v })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Étiquettes — {labelDialog.product?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Upload */}
            <label className="cursor-pointer block">
              <input type="file" className="hidden" accept=".png,.jpg,.jpeg,.pdf"
                onChange={(e) => {
                  if (e.target.files[0] && labelDialog.product) {
                    handleLabelUpload(labelDialog.product.id, e.target.files[0]);
                  }
                  e.target.value = '';
                }} />
              <Button variant="outline" className="w-full gap-2" asChild disabled={uploadingLabel}>
                <span>{uploadingLabel ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />} Ajouter une étiquette</span>
              </Button>
            </label>

            {/* Liste des étiquettes */}
            {(labelDialog.product?.label_files || []).length === 0 && !labelDialog.product?.label_filename && (
              <p className="text-sm text-muted-foreground text-center py-4">Aucune étiquette uploadée</p>
            )}
            {(labelDialog.product?.label_files || []).map((lf) => (
              <div key={lf.id} className="flex items-center gap-2 border border-border rounded-lg p-3">
                <FileText size={16} className="text-primary shrink-0" />
                <span className="text-sm truncate flex-1">{lf.filename}</span>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                  onClick={() => handleLabelDownload(labelDialog.product.id, lf.id, lf.filename)}>
                  <Download size={14} />
                </Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive"
                  onClick={() => handleLabelDelete(labelDialog.product.id, lf.id)}>
                  <Trash2 size={14} />
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Products;
